/**
 * useConversationSeed — owns the "open Messages focused on a specific
 * conversation" flow that used to live in App.tsx.
 *
 * Callers elsewhere in the app (Networking Accept flow, Mentor review
 * list, Create-Group modal) emit the bus events this hook subscribes
 * to. The hook keeps the resulting seed state (dmUser, activeGroupChat)
 * and returns an `initialActive: ConversationSummary | null` ready for
 * `ConversationsView` to consume.
 *
 * App.tsx only wires the hook's `onRoute` callback to switch tab state
 * — everything else is local to this module.
 */

import { useCallback, useEffect, useState } from 'react';
import { on as onAppEvent, emit as emitAppEvent } from '../../core/events/bus';
import { useToastContext } from '../../core/ui/toast/ToastProvider';
import { createGroup, type ConversationSummary } from '../networking/service';
import { mapProfessional } from '../networking/lib';
import type { Professional } from '../../types';

type ActiveGroupChat = {
    id: string;
    name: string;
    members: Professional[];
    lastMessage?: string;
    lastMessageTime?: string;
    unreadCount?: number;
};

export type UseConversationSeedArgs = {
    /** Fired when a bus event requests that the Messages tab become
     *  active. Wire this to the navigation hook's `setActiveTab`. */
    onRouteToMessages: () => void;
};

export type UseConversationSeedReturn = {
    /** Prebuilt `ConversationSummary` for ConversationsView's
     *  `initialActive` prop. `null` when no seed is set. */
    initialActive: ConversationSummary | null;
    /** Explicit DM seeder — used by the Networking "Message" button.
     *  Also switches the active tab via `onRouteToMessages`. */
    openDmWith: (professional: Professional) => void;
    /** Create a group chat and open it. Used by ConversationsView's
     *  `+ New group` flow. */
    createAndOpenGroup: (name: string, members: Professional[]) => Promise<void>;
};

export function useConversationSeed(
    { onRouteToMessages }: UseConversationSeedArgs,
): UseConversationSeedReturn {
    const { addToast } = useToastContext();
    const [dmUser, setDmUser] = useState<Professional | null>(null);
    const [activeGroupChat, setActiveGroupChat] = useState<ActiveGroupChat | null>(null);

    // Cross-module: Networking Accept + Mentor review emit this bus
    // event so the user lands on a seeded DM thread without tab-hunting.
    useEffect(() => {
        return onAppEvent('open-dm-with-user', (payload) => {
            if (!payload?.userId) return;
            const stub: Professional = {
                id: payload.userId,
                name: payload.name ?? 'Member',
                email: '',
                role: '',
                company: '',
                location: '',
                avatar: payload.avatar ?? '',
                online: false,
                skills: [],
                connectionStatus: 'connected',
            };
            setDmUser(stub);
            setActiveGroupChat(null);
            onRouteToMessages();
        });
    }, [onRouteToMessages]);

    const openDmWith = useCallback((professional: Professional) => {
        setDmUser(professional);
        setActiveGroupChat(null);
        onRouteToMessages();
    }, [onRouteToMessages]);

    const createAndOpenGroup = useCallback(async (name: string, members: Professional[]) => {
        const memberIds = members
            .map((m) => Number(m.id))
            .filter((n) => Number.isFinite(n));
        try {
            const created = await createGroup(name, memberIds);
            const newGroup: ActiveGroupChat = {
                id: String(created.id),
                name: created.name || name,
                members: Array.isArray(created.members)
                    ? created.members.map(mapProfessional)
                    : members,
            };
            setActiveGroupChat(newGroup);
            setDmUser(null);
            onRouteToMessages();
            // Nudge the inbox to refetch so the new group shows up in
            // the Rooms folder (the list was loaded before this POST
            // resolved).
            emitAppEvent('group-created', { groupId: newGroup.id });
        } catch (err) {
            console.error('[useConversationSeed] createGroup failed', err);
            addToast("Couldn't create group. Please try again.", 'error');
        }
    }, [addToast, onRouteToMessages]);

    const initialActive: ConversationSummary | null = (() => {
        if (dmUser) {
            const isEmail = typeof dmUser.name === 'string' && dmUser.name.includes('@');
            const displayName = isEmail
                ? (dmUser.name.split('@')[0] || dmUser.name)
                : (dmUser.name || 'Direct message');
            return {
                kind: 'direct',
                id: String(dmUser.id),
                display_name: displayName,
                avatar_url: dmUser.avatar || '',
                last_message: '',
                last_message_at: null,
                unread_count: 0,
            };
        }
        if (activeGroupChat) {
            return {
                kind: 'group',
                id: String(activeGroupChat.id),
                display_name: activeGroupChat.name,
                avatar_url: '',
                last_message: activeGroupChat.lastMessage || '',
                last_message_at: activeGroupChat.lastMessageTime || null,
                unread_count: activeGroupChat.unreadCount || 0,
            };
        }
        return null;
    })();

    return { initialActive, openDmWith, createAndOpenGroup };
}
